import React, {FC} from 'react';
import styled from 'styled-components';

import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useAppDispatch} from '../../../../../../helpers/reduxHelpers';
import {TopicTypeModel} from '../../../../../../models/api/integrationModel';
import {TopicModel} from '../../../../../../models/api/topicModel';
import {
  createAndAddTopicToConversationsAsyncAction,
  removeTopicFromConversationsAction,
} from '../../../../../../store/data/conversations/topicConversationsActions';
import {InboxesVisualConversationSelection} from '../../../../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {IntegrationTopicIcon} from '../../../../../common/integrationTopicIcon';
import {ConversationHeaderPill, StyledPopoverCoordinatorWrapperDiv} from '../conversationHeaderPill';
import {ConversationHeaderPillText} from '../conversationHeaderPillText';
import {TopicDropdownContainer} from './topicDropdownContainer';

/*
 * Props.
 */

interface ConversationHeaderTopicPillProps {
  topic: TopicModel;
  topicType?: TopicTypeModel;
  conversationSelection: InboxesVisualConversationSelection;
}

/*
 * Component.
 */

export const ConversationHeaderTopicPill: FC<ConversationHeaderTopicPillProps> = (props) => {
  const {topic, topicType, conversationSelection} = props;
  const conversationsHandlers = useConversationsHandlers(conversationSelection, topic.id);

  if (!topicType && topic.type !== 'web') {
    return null;
  }

  const renderPopover = (popoverProps: PopoverTransferProps) => (
    <TopicDropdownContainer {...props} {...conversationsHandlers} {...popoverProps} />
  );

  return (
    <StyledPopoverCoordinatorWrapperDiv>
      <PopoverCoordinator
        renderButton={(isOpen) => renderTopicButton(isOpen, props)}
        renderModal={renderPopover}
      />
    </StyledPopoverCoordinatorWrapperDiv>
  );
};

function useConversationsHandlers(
  conversationSelection: InboxesVisualConversationSelection,
  topicId: number,
) {
  const dispatch = useAppDispatch();

  if (!conversationSelection.isFullyKnown) {
    return {};
  }

  const conversationIds = conversationSelection.knownIds;

  return {
    createAndAddTopic: (link: string, name: string) => {
      dispatch(createAndAddTopicToConversationsAsyncAction(conversationIds, link, name));
    },
    removeTopic: () => {
      dispatch(removeTopicFromConversationsAction(conversationIds, topicId));
    },
  };
}

/*
 * Button.
 */

const StyledIntegrationTopicIcon = styled(IntegrationTopicIcon)`
  margin: 0;
  padding: 0;
`;

const StyledTopicIntegrationWrapper = styled.div`
  width: 19px;
  height: 19px;
  display: inline-block;
  margin: 0px -3px -2px -4px;
`;

function renderTopicButton(isOpen: boolean, props: ConversationHeaderTopicPillProps) {
  const {topic} = props;
  const iconRenderer = () => renderIcon(props);
  return (
    <ConversationHeaderPill hasHover={isOpen} renderIcon={iconRenderer} renderTooltip={() => topic.name}>
      <ConversationHeaderPillText>{topic.name}</ConversationHeaderPillText>
    </ConversationHeaderPill>
  );
}

function renderIcon(props: ConversationHeaderTopicPillProps) {
  const {topicType} = props;
  if (!topicType) {
    return <Icon name="topicWebSmall" />;
  }

  return (
    <StyledTopicIntegrationWrapper>
      <StyledIntegrationTopicIcon integrationTopicIconUrl={topicType.image} />
    </StyledTopicIntegrationWrapper>
  );
}
